// =========================================================================
// The hero band the demo put at the top of every section and workflow step,
// and the contour-map background behind it.
//
// `secBG(col)` (demo line 3307) draws a mine plan in three layers, all in the
// section's own colour at very low opacity:
//
//   1. **Hatch** — 14 lines raking up-right at about 72°, opacity 0.035.
//      The drill-pattern texture.
//   2. **Benches** — 4 stepped polylines, each a bench edge dropping away to
//      the right, opacity 0.13 fading to 0.055.
//   3. **Contours** — 9 concentric ellipses rotated −19°, sharing a centre
//      off to the right, opacity 0.17 fading to 0.046. The pit.
//
// Plus a diagonal gradient wash from 0.14 to transparent.
//
// It is ported arithmetic-for-arithmetic rather than approximated, because it
// is the only piece of visual identity these pages have that is not a card.
// The `viewBox` is fixed at 1200×320 with `preserveAspectRatio="none"`, which
// is what makes it stretch to any width and keeps snapshots byte-identical.
//
// One change: the demo generated a new gradient id per call from a module
// counter (`SECBG_N`). A counter is render-order state and would break under
// React's double-invoked development render, so the id comes from `useId()`.
//
// UI layer. Fork-owned (zone C), P5a lane.
// =========================================================================

import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { SeedGlyph } from './SeedGlyph';

/** The nine contour rings, four bench lines and fourteen hatch strokes. */
function ContourBackground({ colour }: { colour: string }) {
  const gradientId = useId();

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 1200 320"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={colour} stopOpacity={0.14} />
          <stop offset="0.5" stopColor={colour} stopOpacity={0.05} />
          <stop offset="1" stopColor={colour} stopOpacity={0} />
        </linearGradient>
      </defs>
      <rect width="1200" height="320" fill={`url(#${gradientId})`} />

      {Array.from({ length: 14 }, (_, i) => (
        <line
          key={`hatch-${i}`}
          x1={-60 + i * 26}
          y1={320}
          x2={40 + i * 26}
          y2={0}
          stroke={colour}
          strokeOpacity={0.035}
          strokeWidth={1}
        />
      ))}

      {[0, 1, 2, 3].map((i) => (
        <path
          key={`bench-${i}`}
          d={`M0 ${252 + i * 20} L120 ${252 + i * 20} L160 ${268 + i * 20} L300 ${268 + i * 20} L344 ${284 + i * 20} L520 ${284 + i * 20}`}
          fill="none"
          stroke={colour}
          strokeOpacity={Number((0.13 - i * 0.025).toFixed(3))}
          strokeWidth={1.1}
        />
      ))}

      {Array.from({ length: 9 }, (_, i) => (
        <ellipse
          key={`ring-${i}`}
          cx={1035}
          cy={150}
          rx={64 + i * 54}
          ry={40 + i * 34}
          fill="none"
          stroke={colour}
          strokeOpacity={Number((0.17 - i * 0.0155).toFixed(3))}
          strokeWidth={1.15}
          transform="rotate(-19 1035 150)"
        />
      ))}
    </svg>
  );
}

/** One figure in a hero's stat strip — the demo's `[eyebrow, value, detail]`. */
export interface HeroStat {
  readonly eyebrow: string;
  readonly value: string;
  readonly detail: string;
}

export interface SectionHeroProps {
  /** Set by `<Commentable>` (cloneElement); forwarded to the root element so the anchor reaches the DOM. */
  'data-comment-anchor'?: string;
  /** The section or module colour; drives the background and the mark. */
  colour: string;
  /** Inline SVG path data for the mark, on a `0 0 24 24` viewBox. */
  icon?: string;
  /** Rendered above the title — the module chip on a step page. */
  eyebrow?: ReactNode;
  title: string;
  /** Printed after the title in muted weight, behind a `·`. */
  tag: string;
  lead: string;
  stats: readonly HeroStat[];
  /** A meta row under a hairline — agents and systems on a step page. */
  meta?: ReactNode;
  /** Sits opposite the title: the help anchor and any step controls. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The demo's `.sechero` / `.wfhero`, unified.
 *
 * The two differed only in the mark: a section page put a 58px glyph tile to
 * the left of the title, a step page put a pill-shaped module chip above it.
 * `icon` selects the first, `eyebrow` the second, and a hero may have both —
 * so one component carries both pages rather than two carrying one each.
 */
export function SectionHero({
  colour,
  icon,
  eyebrow,
  title,
  tag,
  lead,
  stats,
  meta,
  actions,
  className,
  'data-comment-anchor': commentAnchor,
}: SectionHeroProps) {
  return (
    <header
      data-comment-anchor={commentAnchor}
      className={cn('relative min-w-0 overflow-hidden rounded-xl border', className)}
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <ContourBackground colour={colour} />

      <div className="relative flex gap-5 p-6 @lg:px-7">
        {icon ? (
          <span
            aria-hidden="true"
            className="grid h-[58px] w-[58px] shrink-0 place-items-center rounded-[14px] border"
            style={{
              background: `color-mix(in srgb, ${colour} 10%, transparent)`,
              borderColor: `color-mix(in srgb, ${colour} 27%, transparent)`,
            }}
          >
            <SeedGlyph
              markup={icon}
              className="h-[30px] w-[30px]"
              stroke={colour}
              strokeWidth={1.6}
            />
          </span>
        ) : null}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
            <div className="min-w-0">
              {eyebrow ? <div className="mb-2 flex flex-wrap items-center gap-3">{eyebrow}</div> : null}
              <h1
                className="text-[25px] font-semibold leading-tight tracking-[-0.015em]"
                style={{ color: 'var(--ink-primary)' }}
              >
                {title}
                <span
                  className="text-[14px] font-normal tracking-normal"
                  style={{ color: 'var(--ink-tertiary)' }}
                >
                  {' · '}
                  {tag}
                </span>
              </h1>
            </div>
            {actions ? (
              <div className="flex shrink-0 items-center gap-3">{actions}</div>
            ) : null}
          </div>

          <p
            className="mt-1.5 max-w-[74ch] text-[13.5px] leading-[1.5]"
            style={{ color: 'var(--ink-secondary)' }}
          >
            {lead}
          </p>

          {stats.length > 0 ? (
            <dl className="mt-3.5 flex flex-wrap gap-x-[22px] gap-y-3">
              {stats.map((stat) => (
                <div key={stat.eyebrow} className="flex min-w-0 flex-col gap-px">
                  <dt
                    className="text-[9.5px] font-medium uppercase tracking-[0.07em]"
                    style={{ color: 'var(--ink-tertiary)' }}
                  >
                    {stat.eyebrow}
                  </dt>
                  <dd
                    className="mono text-[19px] font-semibold leading-[1.1]"
                    style={{ color: 'var(--ink-primary)' }}
                  >
                    {stat.value}
                  </dd>
                  <dd className="text-[10.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                    {stat.detail}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}

          {meta ? (
            <div
              className="mt-3.5 flex flex-wrap items-center gap-1.5 border-t pt-3"
              style={{ borderColor: 'var(--hairline)' }}
            >
              {meta}
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}

/** The demo's `.wfmeta .lbl` — a small uppercase label inside the meta row. */
export function MetaLabel({ children }: { children: ReactNode }) {
  return (
    <span
      className="mr-0.5 text-[9.5px] font-medium uppercase tracking-[0.07em] [&:not(:first-child)]:ml-2.5"
      style={{ color: 'var(--ink-tertiary)' }}
    >
      {children}
    </span>
  );
}
